import React from 'react';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { Button } from './ui/button';
import { AlertCircle, RefreshCw, X } from 'lucide-react';

interface VersionUpdateNotificationProps {
  /** Auto-refresh after showing notification for this many seconds */
  autoRefreshDelay?: number;
  /** Show debug information */
  debug?: boolean;
}

export function VersionUpdateNotification({ 
  autoRefreshDelay = 5, 
  debug = false 
}: VersionUpdateNotificationProps) {
  const { 
    hasNewVersion, 
    isChecking, 
    refreshApp, 
    currentVersion, 
    error 
  } = useVersionCheck({ 
    checkInterval: 300000, // 5 minutes
    debug 
  });

  const [showNotification, setShowNotification] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const [countdown, setCountdown] = React.useState(autoRefreshDelay);

  // Show notification when new version is detected
  React.useEffect(() => {
    if (hasNewVersion && !dismissed) {
      setShowNotification(true);
      setCountdown(autoRefreshDelay);
    }
  }, [hasNewVersion, dismissed, autoRefreshDelay]);

  // Auto-refresh countdown
  React.useEffect(() => {
    if (!showNotification || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        refreshApp();
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [showNotification, countdown, refreshApp]);

  const handleRefreshNow = () => {
    refreshApp();
  };

  const handleDismiss = () => {
    setShowNotification(false);
    setDismissed(true);
  };

  // Don't render anything if no new version or notification is dismissed
  if (!showNotification) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-blue-500" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            New Version Available
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            A new version of Memara is available. Refresh to get the latest features and improvements.
          </div>
          
          {debug && currentVersion && (
            <div className="text-xs text-gray-400 mt-2 font-mono">
              Version: {currentVersion.version}<br />
              Build: {currentVersion.commitHash}<br />
              {new Date(currentVersion.buildTime).toLocaleString()}
            </div>
          )}

          <div className="flex items-center space-x-2 mt-3">
            <Button
              onClick={handleRefreshNow}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isChecking}
            >
              {isChecking ? (
                <RefreshCw className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Refresh Now
            </Button>
            
            <Button
              onClick={handleDismiss}
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-3 w-3 mr-1" />
              Dismiss
            </Button>
          </div>

          {autoRefreshDelay > 0 && countdown > 0 && (
            <div className="text-xs text-gray-400 mt-2">
              Auto-refreshing in {countdown} seconds...
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-500 mt-2 border-t pt-2">
          Version check error: {error}
        </div>
      )}
    </div>
  );
}

export default VersionUpdateNotification;
